/* eslint-disable no-use-before-define */
import { useCallback, useEffect, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import Form from '../../../components/forms/Form'
import { useNavigate } from 'react-router-dom'
import '../../../assets/styles/custom-styles.css'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import useNotification from '../../../hooks/useNotification'
import {
  transformFormDataForApi,
  cleanPayload,
  getMissingRequiredFields,
  normalizeFormErrors,
} from '../../../utils/objectUtils'
import PageHeader from '../../../components/PageHeader'
import SectionHeader from '../../../components/forms/SectionHeader'
import SimpleBody from '../../../components/forms/SimpleBody'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
} from '@mui/material'
import ItemListSection from '../../../components/forms/ItemListSection'
import MovementEnum from '../../../enums/movement-enum'
import { useOrderServicePageLogic } from './hook/useOrderServicePageLogic'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import DevolutionEquipmentDialog from './DevolutionEquipmentDialog'

const CreateOrderServicePage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showNotification } = useNotification()

  const [contractAmount, setContractAmount] = useState(null)
  const [pendingRecipientChange, setPendingRecipientChange] = useState(null)
  const [devolutionDialogOpen, setDevolutionDialogOpen] = useState(false)
  const [devolutionEquipments, setDevolutionEquipments] = useState([])
  const [devolutionLoading, setDevolutionLoading] = useState(false)
  const [devolutionError, setDevolutionError] = useState(false)

  const {
    form,
    order,
    equipments,
    items,
    extras,
    otherOrganizationsWithCustomerContracts,
    isCheckingOtherOrganizations,
    selectOrgan,
    fetchContractDevolutionEquipments,
    addDevolutionEquipments,
  } = useOrderServicePageLogic({
    isEditPage: false,
    isAddItemPage: false,
    contractAmount,
  })

  const openDevolutionDialog = async () => {
    setDevolutionDialogOpen(true)
    setDevolutionLoading(true)
    setDevolutionError(false)
    try {
      const equipmentList = await fetchContractDevolutionEquipments(
        recipientCustomerId
      )
      setDevolutionEquipments(equipmentList)
    } catch (error) {
      console.error('Erro ao buscar equipamentos para devolução:', error)
      setDevolutionError(true)
    } finally {
      setDevolutionLoading(false)
    }
  }

  const movementTypeId = order.selects.movementType.selectedValue?.value
  const requiresContract =
    !!movementTypeId &&
    movementTypeId !== MovementEnum.AVULSO &&
    movementTypeId !== MovementEnum.DEVOLUCAO
  const recipientCustomerId = order.formData?.order?.recipient_customer_id?.value
  const contractId = order.formData?.order?.contract_id?.value
  const hasContractsInCurrentOrganization =
    (order.selects.contract.rawData?.length ?? 0) > 0
  const isLoadingCurrentOrganizationContracts = order.selects.contract.isFetching
  const displayedOrderFields = [...order.fieldsState].sort((left, right) => {
    const fieldOrder = {
      'order.movement_type_id': 0,
      'order.recipient_customer_id': 1,
      'order.contract_id': 2,
    }
    return (fieldOrder[left.id] ?? 3) - (fieldOrder[right.id] ?? 3)
  })

  const handleOrderFieldChange = useCallback(
    (fieldId, value) => {
      if (
        fieldId === 'order.recipient_customer_id' &&
        requiresContract &&
        recipientCustomerId
      ) {
        const nextRecipientId = value?.value ?? value?.id ?? value ?? null

        if (String(nextRecipientId ?? '') !== String(recipientCustomerId)) {
          setPendingRecipientChange({ value })
          return
        }
      }

      order.handleChange(fieldId, value)
    },
    [requiresContract, order, recipientCustomerId]
  )

  const confirmRecipientChange = useCallback(() => {
    order.handleChange(
      'order.recipient_customer_id',
      pendingRecipientChange?.value ?? null
    )
    setPendingRecipientChange(null)
  }, [order, pendingRecipientChange])

  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
  } = useBaseService(navigate)

  const { data: contractAmountResponse } = useQueryWithLoader({
    queryKey: ['contractAmountDetails', form.contract?.id],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.contracts.contractAmount(form.contract?.id),
        {
          deleted_at: false,
        }
      )
      return response.result
    },
    enabled: !!form.contract?.id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  })

  useEffect(() => {
    setContractAmount(contractAmountResponse)
  }, [contractAmountResponse])

  // Mutações TanStack Query
  const createContractOsMutation = useMutation({
    mutationFn: async (dataToCreate) => {
      if (!form.contract) {
        const response = await baseServicePost(
          entities.serviceOrders.create,
          dataToCreate
        )
        return response
      } else {
        const response = await baseServicePost(
          entities.contracts.contractServiceOrderCreate(form.contract?.id),
          dataToCreate
        )
        return response
      }
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        order.resetOrderServiceForm()

        navigate(-1, {
          state: { type: 'success' },
        })

        // Invalida OrdersServicePage
        queryClient.invalidateQueries({ queryKey: ['orders'] })

        // Invalida CreateOrderServicePage
        queryClient.invalidateQueries({ queryKey: ['contractDetails'] })
        queryClient.invalidateQueries({ queryKey: ['contractItems'] })

        // Invalida ContractOsPage
        queryClient.invalidateQueries({ queryKey: ['contractOrders'] })

        // Invalida CreateMovementPage
        queryClient.invalidateQueries({ queryKey: ['ordersServices'] })
        queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
        queryClient.invalidateQueries({
          queryKey: ['contractItemsForMovement'],
        })
      }
    },
  })

  const handleSubmit = useCallback(async () => {
  try {
    form.setErrors({})

    const missingOrderFields = getMissingRequiredFields({
      itemData: order.formData?.order,
      fields: order.fieldsState,
      prefix: 'order',
    })

    if (missingOrderFields.length > 0) {
      const fieldErrors = {}
      const missingLabels = missingOrderFields.map((fieldId) => {
        const field = order.fieldsState.find((item) => item.id === `order.${fieldId}`)
        const label = field?.label || fieldId
        fieldErrors[`order.${fieldId}`] = `${label} é obrigatório.`
        return label
      })

      form.setErrors(fieldErrors)
      showNotification(
        'warning',
        `Preencha os campos obrigatórios: ${missingLabels.join(', ')}.`
      )
      return
    }

    if (
      equipments.data.length < 1 &&
      items.data.length < 1
    ) {
      showNotification(
        'warning',
        'Adicione pelo menos um equipamento ou produto à requisição antes de cadastrar.'
      )

      return
    }

    const movementTypeId =
      order.selects.movementType.selectedValue?.value

    // ============================================================
    // IMPORTANTE:
    // Os itens adicionados nas listas vêm de .data
    // e não de order.formData
    // ============================================================
    const formDataToApi = {
      ...order.formData,
      equipments: equipments.data ?? [],
      items: items.data ?? [],
      extras: extras.data ?? [],
    }

    console.log('========== ANTES DA TRANSFORMAÇÃO ==========')
    console.log('equipments.data:', equipments.data)
    console.log('items.data:', items.data)
    console.log('extras.data:', extras.data)
    console.log('============================================')

    const transformedFormData =
      transformFormDataForApi(formDataToApi)

    const cleanedPayload = cleanPayload(transformedFormData, {
      skipNumberConversionFields: ['order.details'],
    })

    const getPrimitiveId = (val) => {
      if (val === null || val === undefined) return null

      if (typeof val === 'object') {
        return val.value ?? val.id ?? null
      }

      return val
    }

    // ============================================================
    // NORMALIZA OS EXTRAS DIRETAMENTE DE extras.data
    // Não depende de order.formData.extras
    // ============================================================
    const normalizedExtras = (extras.data ?? []).map((extra) => ({
      id: extra.id ?? null,
      equipment_id: getPrimitiveId(extra.equipment_id),
      type_id: getPrimitiveId(extra.type_id),
      quantity: Number(extra.quantity) || 1,
    }))

    console.log('========== EXTRAS NORMALIZADOS ==========')
    console.log(normalizedExtras)
    console.log('==========================================')

    const payload = {
  ...cleanedPayload,

  order: {
    ...cleanedPayload.order,
    organization_id: form.orgao?.id,
    movement_type_id: movementTypeId,
    requires_invoice: Boolean(
      Number(getPrimitiveId(cleanedPayload.order?.requires_invoice) ?? 0)
    ),
  },

  equipments: (equipments.data ?? []).map((equipment) => {
    const eqId = getPrimitiveId(equipment.id)
    const eqContractItemId = getPrimitiveId(equipment.contract_item_id)

    const relatedExtras = normalizedExtras
      .filter((extra) => {
        const extraEquipmentId = String(extra.equipment_id)
        return (
          extraEquipmentId === String(eqId) ||
          extraEquipmentId === String(eqContractItemId)
        )
      })
      .map((extra) => ({
        type_id: Number(extra.type_id),
        quantity: Number(extra.quantity) || 1,
      }))

    // ✅ WHITELIST: só campos que o backend espera, todos primitivos
    return {
      contract_item_id: eqContractItemId,
      equipment_id: getPrimitiveId(equipment.equipment_id),
      type_id: getPrimitiveId(equipment.type_id),
      quantity: Number(equipment.quantity) || 0,
      department_id: getPrimitiveId(equipment.department_id),
      details: equipment.details ?? null,
      movement_type_id: Number(movementTypeId) || null,
      is_exchange: Boolean(equipment.is_exchange),
      is_extra: relatedExtras.length > 0,
      extras: relatedExtras,
      address_id:
        getPrimitiveId(cleanedPayload.order?.address_id) ??
        getPrimitiveId(equipment.address_id),
      location_id:
        getPrimitiveId(cleanedPayload.order?.location_id) ??
        getPrimitiveId(equipment.location_id),
    }
  }),

  items: (items.data ?? []).map((item) => ({
    product_id: getPrimitiveId(item.product_id),
    quantity_product: Number(item.quantity_product) || 0,
    department_id: getPrimitiveId(item.department_id),
    details: item.details ?? null,
    movement_type_id: Number(movementTypeId) || null,
    address_id:
      getPrimitiveId(cleanedPayload.order?.address_id) ??
      getPrimitiveId(item.address_id),
    location_id:
      getPrimitiveId(cleanedPayload.order?.location_id) ??
      getPrimitiveId(item.location_id),
  })),
}

delete payload.extras // os extras já vão aninhados em cada equipment

    console.log(
      '🚀 PAYLOAD FINAL:',
      JSON.stringify(payload, null, 2)
    )

    console.log(
      '🔎 EXTRAS POR EQUIPAMENTO:',
      payload.equipments.map((equipment) => ({
        id: equipment.id,
        contract_item_id: getPrimitiveId(
          equipment.contract_item_id
        ),
        is_exchange: equipment.is_exchange,
        is_extra: equipment.is_extra,
        extras: equipment.extras,
      }))
    )

    await createContractOsMutation.mutateAsync(payload)

  } catch (error) {
    console.error('Erro ao enviar requisição:', {
      status: error.status,
      message: error.message,
      errors: error.response?.data?.errors,
    })
  }
}, [
  createContractOsMutation,
  equipments.data,
  extras.data,
  form,
  items.data,
  order.formData,
  order.fieldsState,
  order.selects.movementType.selectedValue?.value,
  showNotification,
])

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos/${form.contract?.id}/requisicoes/`)
  }, [navigate, form.contract?.id])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title={`Cadastro de Requisição`}
        showBackButton={true}
        backUrl={`/aluguel/contratos/${form.contract?.id}/requisicoes/`}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          handleBack={handleBack}
          isLoading={createContractOsMutation.isPending}>
          {() => (
            <>
              {requiresContract && !recipientCustomerId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Escolha o cliente destinatário para liberar a seleção de
                  contrato.
                </Alert>
              )}
              {requiresContract &&
                recipientCustomerId &&
                !contractId &&
                !hasContractsInCurrentOrganization &&
                !isLoadingCurrentOrganizationContracts &&
                !isCheckingOtherOrganizations &&
                otherOrganizationsWithCustomerContracts.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      Este cliente não possui contratos na organização{' '}
                      {form.orgao?.name || 'atual'}.
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                      Há contratos disponíveis em:{' '}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {otherOrganizationsWithCustomerContracts.map((organization) => (
                        <Button
                          key={organization.id}
                          size="small"
                          variant="contained"
                          onClick={() => selectOrgan(organization)}>
                          Trocar para {organization.alias || organization.name}
                        </Button>
                      ))}
                    </Box>
                  </Alert>
                )}
              <div key={order.section}>
                <SectionHeader section={order.section} />
                <SimpleBody
                  fields={displayedOrderFields.map((field) => ({
                    ...field,
                    label:
                      field.notRequired === false && !field.disabled
                        ? `${field.label} *`
                        : field.label,
                  }))}
                  formErrors={normalizeFormErrors(formErrors)}
                  formData={order.formData}
                  handleFieldChange={handleOrderFieldChange}
                  getOptions={order.getOptions}
                  getSelectedValue={order.getSelectedValue}
                  getSelectMeta={order.getSelectMeta}
                />
              </div>
              <Box
                key={equipments.section}
                sx={{ mt: '1rem', display: 'flex', flexDirection: 'column' }}>
                {movementTypeId === MovementEnum.DEVOLUCAO && recipientCustomerId && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button variant="outlined" onClick={openDevolutionDialog} disabled={devolutionLoading} startIcon={devolutionLoading ? <CircularProgress size={16} /> : undefined}>
                      Selecionar equipamentos deste cliente
                    </Button>
                  </Box>
                )}
                {/* EQUIPAMENTOS */}
                <ItemListSection
                  section={equipments.section}
                  fields={equipments.fieldsState}
                  initialData={equipments.data}
                  onItemsChange={equipments.onItemsChange}
                  renderFormBody={equipments.renderFormBody}
                  getItemDisplayValue={equipments.getItemDisplayValue}
                  itemFormData={equipments.formData}
                  onItemChange={equipments.handleChange}
                  itemFormErrors={normalizeFormErrors(formErrors)}
                  onFormSubmit={equipments.onFormSubmit}
                  onDeleteItem={equipments.onDeleteItem}
                  initializeItemForm={equipments.initializeItemForm}
                  isDisabledButton={form.disabledButton}
                  getOptions={equipments.getOptions}
                  getSelectedValue={equipments.getSelectedValue}
                  getSelectMeta={equipments.getSelectMeta}
                  captureAction={equipments.captureAction}
                  notShowHeader={[
                    'equipments.location_id',
                    'equipments.details',
                  ]}
                  headerCustom={() => {
                    if (
                      order.selects.movementType.selectedValue?.value !==
                      MovementEnum.ALUGUEL
                    )
                      return null
                    return (
                      <>
                        <br />
                        {!order.hasBalance && (
                          <Alert severity="warning">
                            <Typography sx={{ fontSize: '1rem' }}>
                              Todos os itens do contrato já foram atendidos e o
                              saldo mensal está esgotado. Novos itens serão
                              considerados como excedente de contrato. Será
                              necessário uma autorização da gerência para essa
                              requisição.
                            </Typography>
                          </Alert>
                        )}
                        <hr />
                      </>
                    )
                  }}
                />
              </Box>

              {/* Somente para aluguel */}
              {[MovementEnum.ALUGUEL, MovementEnum.RESERVA].includes(
                order.selects.movementType.selectedValue?.value
              ) && (
                <>
                  <Box
                    key={extras.section}
                    sx={{
                      mt: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* KIT DE EQUIPAMENTOS */}
                    <ItemListSection
                      section={extras.section}
                      fields={extras.fields}
                      initialData={extras.data}
                      onItemsChange={extras.onItemsChange}
                      renderFormBody={extras.renderFormBody}
                      getItemDisplayValue={extras.getItemDisplayValue}
                      itemFormData={extras.formData}
                      onItemChange={extras.handleChange}
                      itemFormErrors={normalizeFormErrors(formErrors)}
                      onFormSubmit={extras.onFormSubmit}
                      initializeItemForm={extras.initializeItemForm}
                      isDisabledButton={form.disabledButton}
                      getOptions={extras.getOptions}
                      getSelectedValue={extras.getSelectedValue}
                      getSelectMeta={extras.getSelectMeta}
                      captureAction={extras.captureAction}
                    />
                  </Box>
                </>
              )}

              {/* ITEMS */}
              {[
                MovementEnum.ALUGUEL,
                MovementEnum.RESERVA,
                MovementEnum.AVULSO,
              ].includes(order.selects.movementType.selectedValue?.value) && (
                <>
                  <Box
                    key={items.section}
                    sx={{
                      mt: '1rem',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                    {/* ITEMS */}
                    <ItemListSection
                      section={items.section}
                      fields={items.fields}
                      initialData={items.data}
                      onItemsChange={items.onItemsChange}
                      renderFormBody={items.renderFormBody}
                      getItemDisplayValue={items.getItemDisplayValue}
                      itemFormData={items.formData}
                      onItemChange={items.handleChange}
                      itemFormErrors={normalizeFormErrors(formErrors)}
                      onFormSubmit={items.onFormSubmit}
                      initializeItemForm={items.initializeItemForm}
                      captureAction={items.captureAction}
                      isDisabledButton={form.disabledButton}
                      getOptions={items.getOptions}
                      getSelectedValue={items.getSelectedValue}
                      getSelectMeta={items.getSelectMeta}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </Form>
        <Dialog
          open={pendingRecipientChange !== null}
          onClose={() => setPendingRecipientChange(null)}
        >
          <DialogTitle>Trocar cliente destinatário?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Ao trocar o cliente, o contrato, o endereço, o prazo e os itens
              dependentes da seleção atual serão redefinidos.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingRecipientChange(null)}>
              Manter cliente atual
            </Button>
            <Button onClick={confirmRecipientChange} variant="contained">
              Trocar cliente
            </Button>
          </DialogActions>
        </Dialog>
        <DevolutionEquipmentDialog
          open={devolutionDialogOpen}
          onClose={() => setDevolutionDialogOpen(false)}
          onConfirm={addDevolutionEquipments}
          equipments={devolutionEquipments}
          loading={devolutionLoading}
          error={devolutionError}
          alreadyPresentIds={equipments.data.map((item) =>
            item.equipment_id?.value ?? item.equipment_id?.id ?? item.equipment_id
          ).filter((id) => id !== null && id !== undefined)}
        />
      </div>
    </MainLayout>
  )
}

export default CreateOrderServicePage
